import React, { useEffect, useRef, useState } from 'react'
import { Button } from './Button'
import { uploadImage } from '../../lib/supabase/storage'

interface RichTextEditorProps {
  label?: string
  value: string
  onChange: (value: string) => void
  placeholder?: string
  minHeightClassName?: string
}

const exec = (command: string) => {
  document.execCommand(command)
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  label,
  value,
  onChange,
  placeholder,
  minHeightClassName = 'min-h-[160px]',
}) => {
  const editorRef = useRef<HTMLDivElement | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [isUploading, setIsUploading] = useState(false)

  useEffect(() => {
    if (!editorRef.current) {
      return
    }

    if (editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || ''
    }
  }, [value])

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setIsUploading(true)
    try {
      const imageUrl = await uploadImage(file, 'notes')
      
      // Insert image into editor
      const img = `<img src="${imageUrl}" alt="uploaded image" style="max-width: 100%; height: auto; margin: 8px 0;" />`
      document.execCommand('insertHTML', false, img)
      
      // Trigger onChange to save
      if (editorRef.current) {
        onChange(editorRef.current.innerHTML)
      }
    } catch (error) {
      console.error('Failed to upload image:', error)
      alert('Failed to upload image. Please try again.')
    } finally {
      setIsUploading(false)
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  return (
    <div className="space-y-2">
      {label && <span className="text-sm font-medium text-gray-700">{label}</span>}
      <div className="rounded-lg border border-gray-300">
        <div className="flex flex-wrap gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2">
          <Button type="button" size="sm" variant="secondary" onClick={() => exec('bold')}>Bold</Button>
          <Button type="button" size="sm" variant="secondary" onClick={() => exec('italic')}>Italic</Button>
          <Button type="button" size="sm" variant="secondary" onClick={() => exec('insertUnorderedList')}>Bullets</Button>
          <Button type="button" size="sm" variant="secondary" onClick={() => exec('insertOrderedList')}>Numbering</Button>
          <Button 
            type="button" 
            size="sm" 
            variant="secondary" 
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
          >
            {isUploading ? 'Uploading...' : 'Add Image'}
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={() => exec('removeFormat')}>Clear</Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            style={{ display: 'none' }}
          />
        </div>
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          className={`prose prose-sm max-w-none px-3 py-2 outline-none ${minHeightClassName}`}
          data-placeholder={placeholder}
          onInput={(event) => onChange(event.currentTarget.innerHTML)}
        />
      </div>
    </div>
  )
}